import { Vector2, Vector3, Vector4, Quaternion, Matrix3, Matrix4 } from "flavr-math";
export const NUMBER_DECIMALS = 3;
export const HIGHLIGHT_TIMEOUT_MS = 3000;
const NUMBER_COMPARE_EPSILON = 0.5 * Math.pow(10, (-NUMBER_DECIMALS));
export const buildModelTree = (model, prevTreeImmutable, time, autoUpdate) => {
    var _a;
    const prevTree = prevTreeImmutable !== undefined ? cloneModelTree(prevTreeImmutable) : createModelTree();
    const curTree = createModelTree();
    // Persist deleted models
    curTree.deletedModels = new Map();
    for (const deletedModel of (_a = prevTree === null || prevTree === void 0 ? void 0 : prevTree.deletedModels.values()) !== null && _a !== void 0 ? _a : []) {
        const age = deletedModel.changedAt !== undefined ? time - deletedModel.changedAt : Infinity;
        if (age <= HIGHLIGHT_TIMEOUT_MS) {
            curTree.deletedModels.set(deletedModel.uid, deletedModel);
        }
    }
    for (const deletedModel of curTree.deletedModels.values()) {
        curTree.nodeMap.set(deletedModel.uid, deletedModel);
    }
    if (model === undefined) {
        return curTree;
    }
    // Iterate through model hierarchy and populate deletedModels as well as nodeMap
    curTree.root = buildTreeRecursive(model, undefined, curTree, prevTree, time, autoUpdate);
    return curTree;
};
export const updateModelTree = (uid, model, prevTreeImmutable, time, autoUpdate) => {
    var _a, _b, _c, _d;
    const curTree = cloneModelTree(prevTreeImmutable);
    const prevNode = prevTreeImmutable.nodeMap.get(uid);
    if (prevNode === undefined && model === undefined)
        return curTree;
    // Created a new node
    if (prevNode === undefined) {
        const curNode = createModelNode(model, undefined, time, autoUpdate);
        const parentUID = (_b = (_a = model === null || model === void 0 ? void 0 : model.getParent()) === null || _a === void 0 ? void 0 : _a.getUID()) !== null && _b !== void 0 ? _b : -1;
        const parentNode = curTree.nodeMap.get(parentUID);
        if (parentNode !== undefined) {
            parentNode.childNodes.push(curNode);
        }
    }
    // Removed a node
    else if (model === undefined) {
        const curNode = curTree.nodeMap.get(uid);
        if (curNode !== undefined) {
            const parentUID = (_d = (_c = curNode.parentNode) === null || _c === void 0 ? void 0 : _c.uid) !== null && _d !== void 0 ? _d : -1;
            const parentNode = curTree.nodeMap.get(parentUID);
            if (parentNode !== undefined) {
                parentNode.childNodes = parentNode.childNodes.filter(n => n.uid !== curNode.uid);
            }
            if (curNode.childNodes.length > 0) {
                console.warn("[ui] Removed node still has children. How to deal with them???");
            }
            curTree.deletedModels.set(curNode.uid, curNode);
        }
    }
    // Updating a node
    else {
        const curNode = curTree.nodeMap.get(uid);
        // Update values
        if (curNode !== undefined) {
            const createdNode = createModelNode(model, prevNode, time, autoUpdate);
            curNode.uid = createdNode.uid;
            curNode.name = createdNode.name;
            curNode.room = createdNode.room;
            curNode.parent = createdNode.parent;
            curNode.properties = createdNode.properties;
            curNode.facets = createdNode.facets;
            propagateChangeType(curNode, createdNode, time, autoUpdate);
        }
        if ((curNode === null || curNode === void 0 ? void 0 : curNode.room) !== prevNode.room || (curNode === null || curNode === void 0 ? void 0 : curNode.parent) !== prevNode.parent) {
            console.warn('[ui] Room or parent of node changed. How to deal with them???');
        }
    }
    return curTree;
};
export const cloneModelTree = (prev) => {
    const curTree = createModelTree();
    if (prev.root !== undefined)
        curTree.root = cloneModelNodeRecursive(curTree, prev.root);
    prev.deletedModels.forEach(model => {
        const deletedModel = curTree.nodeMap.get(model.uid);
        if (deletedModel !== undefined)
            curTree.deletedModels.set(deletedModel.uid, deletedModel);
    });
    return curTree;
};
/// Node creation ///
const buildTreeRecursive = (model, prevNode, curTree, prevTree, time, autoUpdate) => {
    var _a, _b;
    prevNode = prevNode !== null && prevNode !== void 0 ? prevNode : ((model !== undefined) ? prevTree === null || prevTree === void 0 ? void 0 : prevTree.nodeMap.get(model.getUID()) : undefined);
    const curNode = createModelNode(model, prevNode, time, autoUpdate);
    // Update deleted models
    if (curNode.changeType === "created") {
        curTree.deletedModels.delete(curNode.uid);
    }
    else if (curNode.changeType === "deleted") {
        curTree.deletedModels.set(curNode.uid, curNode);
    }
    // Call recursive for all existing and newly children
    const curChildren = deduplicateModelsByUid((_a = model === null || model === void 0 ? void 0 : model.getChildren()) !== null && _a !== void 0 ? _a : []);
    curNode.childNodes = curChildren.map(child => buildTreeRecursive(child, undefined, curTree, prevTree, time, autoUpdate));
    // Call recursive for all deleted children
    const currentChildUIDs = new Set(curChildren.map(child => child.getUID()));
    const prevChildren = (_b = prevNode === null || prevNode === void 0 ? void 0 : prevNode.childNodes.filter(child => {
        if (child.changeType !== "deleted")
            return true;
        const age = child.changedAt !== undefined ? time - child.changedAt : Infinity;
        return age <= HIGHLIGHT_TIMEOUT_MS;
    }).filter(child => !currentChildUIDs.has(child.uid)).map(child => buildTreeRecursive(undefined, child, curTree, prevTree, time, autoUpdate))) !== null && _b !== void 0 ? _b : [];
    curNode.childNodes.push(...prevChildren);
    curNode.childNodes = deduplicateChildNodesByUid(curNode.childNodes);
    curTree.nodeMap.set(curNode.uid, curNode);
    return curNode;
};
const deduplicateModelsByUid = (models) => {
    const seen = new Set();
    const uniqueModels = [];
    for (const model of models) {
        const uid = model.getUID();
        if (seen.has(uid))
            continue;
        seen.add(uid);
        uniqueModels.push(model);
    }
    return uniqueModels;
};
const deduplicateChildNodesByUid = (nodes) => {
    const seen = new Set();
    const uniqueNodes = [];
    for (const node of nodes) {
        if (seen.has(node.uid))
            continue;
        seen.add(node.uid);
        uniqueNodes.push(node);
    }
    return uniqueNodes;
};
export const cloneModelNodeRecursive = (curTree, node) => {
    var _a;
    const curNode = {
        uid: node.uid,
        name: node.name,
        room: node.room,
        parent: node.parent,
        createdAt: node.createdAt,
        modifiedAt: node.modifiedAt,
        properties: cloneProperties(node.properties),
        facets: cloneFacets(node.facets),
        parentNode: undefined,
        childNodes: [],
        changedAt: node.changedAt,
        changeType: node.changeType,
        expanded: node.expanded
    };
    curTree.nodeMap.set(curNode.uid, curNode);
    curNode.parentNode = node.parentNode !== undefined ? curTree.nodeMap.get(node.parentNode.uid) : undefined;
    curNode.childNodes = (_a = node === null || node === void 0 ? void 0 : node.childNodes.map(n => cloneModelNodeRecursive(curTree, n))) !== null && _a !== void 0 ? _a : [];
    return curNode;
};
/// Initialization ///
export const createModelTree = () => {
    return {
        root: undefined,
        deletedModels: new Map(),
        nodeMap: new Map(),
    };
};
const createModelNode = (model, prevNode, time, autoUpdate) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const properties = createProperties(model, prevNode, time, autoUpdate);
    const facets = createFacetInfos(model, prevNode, time, autoUpdate);
    const curNode = {
        uid: (_b = (_a = model === null || model === void 0 ? void 0 : model.getUID()) !== null && _a !== void 0 ? _a : prevNode === null || prevNode === void 0 ? void 0 : prevNode.uid) !== null && _b !== void 0 ? _b : -1,
        name: (_d = (_c = model === null || model === void 0 ? void 0 : model.getName()) !== null && _c !== void 0 ? _c : prevNode === null || prevNode === void 0 ? void 0 : prevNode.name) !== null && _d !== void 0 ? _d : "unknown",
        room: (_g = (_f = (_e = model === null || model === void 0 ? void 0 : model.getRoom()) === null || _e === void 0 ? void 0 : _e.getName()) !== null && _f !== void 0 ? _f : prevNode === null || prevNode === void 0 ? void 0 : prevNode.room) !== null && _g !== void 0 ? _g : "unknown",
        parent: (_k = (_j = (_h = model === null || model === void 0 ? void 0 : model.getParent()) === null || _h === void 0 ? void 0 : _h.getName()) !== null && _j !== void 0 ? _j : prevNode === null || prevNode === void 0 ? void 0 : prevNode.parent) !== null && _k !== void 0 ? _k : "unknown",
        createdAt: prevNode === null || prevNode === void 0 ? void 0 : prevNode.createdAt,
        modifiedAt: prevNode === null || prevNode === void 0 ? void 0 : prevNode.modifiedAt,
        childNodes: [],
        properties,
        facets,
        changedAt: undefined,
        changeType: "unchanged",
        expanded: (_l = prevNode === null || prevNode === void 0 ? void 0 : prevNode.expanded) !== null && _l !== void 0 ? _l : false,
    };
    if (model === undefined) {
        if ((prevNode === null || prevNode === void 0 ? void 0 : prevNode.changeType) === "deleted") {
            propagateChangeType(curNode, prevNode, time, autoUpdate);
        }
        else {
            curNode.changedAt = time;
            curNode.changeType = "deleted";
        }
    }
    else if (prevNode === undefined) {
        curNode.createdAt = time;
        curNode.modifiedAt = time;
        curNode.changedAt = time;
        curNode.changeType = "created";
    }
    else {
        const propertiesModified = properties.some(prop => prop.changeType === "updated");
        const facetsModified = facets.some(prop => prop.changeType === "updated");
        const roomModified = curNode.room !== (prevNode === null || prevNode === void 0 ? void 0 : prevNode.room);
        const parentModified = curNode.parent !== (prevNode === null || prevNode === void 0 ? void 0 : prevNode.parent);
        const nodeModified = propertiesModified || facetsModified || roomModified || parentModified;
        if (nodeModified) {
            curNode.modifiedAt = time;
            curNode.changedAt = time;
            curNode.changeType = "updated";
        }
        else {
            propagateChangeType(curNode, prevNode, time, autoUpdate);
        }
    }
    return curNode;
};
/// Facet helper methods ///
const createFacetInfos = (model, prevNode, time, autoUpdate) => {
    var _a;
    if (model === undefined)
        return [];
    const facetInfos = [];
    for (const facet of model.getFacets()) {
        const properties = Object
            .keys(facet)
            .filter(key => Object.prototype.hasOwnProperty.call(facet, key))
            .map(key => resolveProperty(key, facet[key]));
        facetInfos.push({
            name: facet.type,
            properties,
            changedAt: undefined,
            changeType: "unchanged"
        });
    }
    // Create maps for faster look up in the next step
    const curFacetsMap = new Map();
    for (const facet of facetInfos) {
        curFacetsMap.set(facet.name, facet);
    }
    const prevFacets = (_a = prevNode === null || prevNode === void 0 ? void 0 : prevNode.facets) !== null && _a !== void 0 ? _a : [];
    const prevFacetsMap = new Map();
    for (const facet of prevFacets) {
        prevFacetsMap.set(facet.name, facet);
    }
    // Created facets
    facetInfos
        .filter(info => !prevFacetsMap.has(info.name))
        .forEach(info => {
        info.changedAt = time;
        info.changeType = "created";
    });
    // Updated facets
    facetInfos
        .filter(info => prevFacetsMap.has(info.name))
        .forEach(info => {
        var _a;
        const prevFacet = prevFacetsMap.get(info.name);
        const prevProperties = (_a = prevFacet === null || prevFacet === void 0 ? void 0 : prevFacet.properties) !== null && _a !== void 0 ? _a : [];
        diffFacetProperties(info.properties, prevProperties, time, autoUpdate);
        const propertiesModified = info.properties.some(prop => prop.changeType !== "unchanged");
        if (propertiesModified) {
            info.changedAt = time;
            info.changeType = "updated";
        }
        else {
            propagateChangeType(info, prevFacet, time, autoUpdate);
        }
    });
    // Deleted facets
    prevFacets
        .filter(info => !curFacetsMap.has(info.name))
        .forEach(info => {
        info.changedAt = time;
        info.changeType = "deleted";
        facetInfos.push(info);
    });
    return facetInfos;
};
const cloneFacets = (facets) => {
    return facets.map(cloneFacet);
};
const cloneFacet = (facet) => {
    return {
        name: facet.name,
        properties: cloneProperties(facet.properties),
        changedAt: facet.changedAt,
        changeType: facet.changeType
    };
};
const diffFacetProperties = (curProperties, prevProperties, time, autoUpdate) => {
    const curProps = curProperties !== null && curProperties !== void 0 ? curProperties : [];
    const curPropsMap = new Map();
    for (const prop of curProps) {
        curPropsMap.set(prop.name, prop);
    }
    const prevProps = prevProperties !== null && prevProperties !== void 0 ? prevProperties : [];
    const prevPropsMap = new Map();
    for (const prop of prevProps) {
        prevPropsMap.set(prop.name, prop);
    }
    // Created new props
    curProps
        .filter(prop => !prevPropsMap.has(prop.name))
        .forEach(prop => {
        prop.changedAt = time;
        prop.changeType = "created";
    });
    // Updated props
    curProps
        .filter(prop => prevPropsMap.has(prop.name))
        .forEach(prop => {
        const prevProp = prevPropsMap.get(prop.name);
        const updatedProp = diffProperty(prop, prevProp, time, autoUpdate);
        const propertiesModified = updatedProp !== undefined && updatedProp.changeType !== "unchanged";
        if (propertiesModified) {
            prop.changedAt = time;
            prop.changeType = "updated";
        }
        else {
            propagateChangeType(prop, prevProp, time, autoUpdate);
        }
    });
    // Deleted props
    prevProps
        .filter(prop => !curPropsMap.has(prop.name))
        .forEach(prop => {
        prop.changedAt = time;
        prop.changeType = "deleted";
    });
};
/// Property helper methods ///
const createProperties = (model, prevNode, time, autoUpdate) => {
    var _a, _b, _c;
    if (model === undefined)
        return [];
    const values = {
        uid: model.getUID(),
        name: model.getName(),
        parent: (_a = model.getParent()) === null || _a === void 0 ? void 0 : _a.getUID(),
        room: (_b = model.getRoom()) === null || _b === void 0 ? void 0 : _b.getUID(),
        visible: model.isVisible(),
        transform: model.getTransformComponents(),
        worldTransform: model.getWorldTransformComponents(),
    };
    let properties = Object.entries(values).map(([key, value]) => resolveProperty(key, value));
    if (properties.length === 0) {
        properties = (_c = prevNode === null || prevNode === void 0 ? void 0 : prevNode.properties) !== null && _c !== void 0 ? _c : [];
        properties.forEach(prop => { prop.changeType = "deleted"; prop.changedAt = time; });
    }
    else if (prevNode === undefined) {
        properties.forEach(prop => { prop.changeType = "created"; prop.changedAt = time; });
    }
    else {
        const prevProperties = prevNode.properties;
        properties = properties.map((prop, i) => diffProperty(prop, prevProperties[i], time, autoUpdate));
    }
    return properties;
};
const cloneProperties = (props) => {
    return props.map(cloneProperty);
};
const resolveProperty = (name, value) => {
    let resolvedValue;
    if (typeof value === "boolean")
        resolvedValue = String(value);
    else if (typeof value === "string")
        resolvedValue = value;
    else if (typeof value === "number")
        resolvedValue = formatNumber(value);
    else if (typeof value === "function")
        resolvedValue = "Function";
    else if (typeof value === "undefined")
        resolvedValue = `undefined`;
    else if (value instanceof Vector2)
        resolvedValue = value.toArray();
    else if (value instanceof Vector3)
        resolvedValue = value.toArray();
    else if (value instanceof Vector4)
        resolvedValue = value.toArray();
    else if (value instanceof Quaternion)
        resolvedValue = value.toArray();
    else if (value instanceof Matrix3)
        resolvedValue = value.toArray();
    else if (value instanceof Matrix4)
        resolvedValue = Array.from(value.toArray());
    else if (Array.isArray(value)) {
        if (value.every(v => typeof v === "number"))
            resolvedValue = value;
        else
            resolvedValue = value.map(((v, i) => resolveProperty(`${i}`, v)));
    }
    else if (typeof value === 'object') {
        resolvedValue = [];
        for (const key in value) {
            if (Object.prototype.hasOwnProperty.call(value, key)) {
                const childProperty = resolveProperty(key, value[key]);
                resolvedValue.push(childProperty);
            }
        }
    }
    else {
        throw new Error(`[ui] Cannot resolve property ${name}: ${value}, no registered format implemented yet`);
    }
    return {
        name,
        value: resolvedValue,
        changedAt: undefined,
        changeType: "unchanged"
    };
};
const diffProperty = (property, prevProperty, time, autoUpdate) => {
    if (property === undefined && prevProperty === undefined)
        throw new Error("[ui] Both property and prevProperty are undefined");
    if (prevProperty === undefined)
        return Object.assign(Object.assign({}, property), { changedAt: time, changeType: "created" });
    if (property === undefined)
        return Object.assign(Object.assign({}, prevProperty), { changedAt: time, changeType: "deleted" });
    // Do the actual diffing
    const curValue = property.value;
    const prevValue = prevProperty.value;
    let noChange = true;
    if (typeof curValue === "string")
        noChange = curValue === prevValue;
    else
        noChange = curValue.length === prevValue.length && (isNumberArray(curValue)
            ? curValue.every((v, i) => numbersAppearEqual(v, prevValue[i])) // either both values are number[]
            : curValue.every((v, i) => // or Property[]
             isPropertyUnchanged(v, prevValue[i], time, autoUpdate)));
    if (!noChange) {
        return Object.assign(Object.assign({}, property), { changedAt: time, changeType: "updated" });
    }
    const changedProperty = Object.assign(Object.assign({}, property), { changedAt: noChange ? undefined : time, changeType: noChange ? "unchanged" : "updated" });
    propagateChangeType(changedProperty, prevProperty, time, autoUpdate);
    return changedProperty;
};
const cloneProperty = (property) => {
    let value = "";
    if (typeof property.value === "string")
        value = property.value;
    else if (isNumberArray(property.value))
        value = [...property.value];
    else
        value = cloneProperties(property.value);
    return {
        name: property.name,
        value,
        changedAt: property.changedAt,
        changeType: property.changeType
    };
};
const isPropertyUnchanged = (prop, prevProp, time, autoUpdate) => {
    return diffProperty(prop, prevProp, time, autoUpdate).changeType === "unchanged";
};
const numbersAppearEqual = (a, b) => {
    return Math.abs(a - b) < NUMBER_COMPARE_EPSILON;
};
/// Handle change propagation ///
const propagateChangeType = (comp, prevComp, time, autoUpdate) => {
    const reset = prevComp.changeType !== "deleted" // never remove the deleted status here
        && (!autoUpdate // if we manually update we want to reset the value each time
            || (prevComp.changedAt !== undefined && (time - prevComp.changedAt) > HIGHLIGHT_TIMEOUT_MS)); // otherwise we wait a few seconds
    comp.changedAt = reset ? time : prevComp.changedAt;
    comp.changeType = reset ? "unchanged" : prevComp.changeType;
};
/// Formating ///
const formatNumber = (value) => {
    if (Number.isInteger(value))
        return `${value}`;
    return value.toFixed(NUMBER_DECIMALS);
};
/// Type guards ///
export function isNumberArray(a) {
    return Array.isArray(a) && a.every(v => typeof v === "number");
}
